import type { TourEvent } from '@/features/events';
import { formatDayLabel, formatLongDate } from '@/features/events';
import { Section } from '@/components/layout';
import { ProgramItem } from '@/components/ui';
import { Reveal } from '@/components/motion';
import { MEXICO_COPY } from '../copy';
import { ArrowRule } from '../ornaments/ArrowRule';
import styles from './ProgramaMexico.module.css';

const TITLE_ID = 'mexico-programa';

/** Ornamento por bloque, en el orden del programa. Puro adorno, no dato. */
const BLOCK_ICONS = ['spiral', 'star', 'sun', 'seed'] as const;

export interface ProgramaMexicoProps {
  event: TourEvent;
}

/**
 * S4.bis · PROGRAMA COMPLETO DE DOS DÍAS.
 *
 * ⚠️ ADICIÓN EXIGIDA POR EL BRIEF (ADR-006 · master §7 · `07_ARQUITECTURA_LANDINGS.md` §5).
 * `DEMO_4_Mexico.png` NO dibuja el programa: rotula "un viaje de dos días" con
 * dos titulares de jornada y nada más. Eso deja a un asistente comprando dos
 * días sin saber qué pasa cada día ni a qué hora — el hueco más grave que
 * encontró el ORACLE en las seis páginas.
 *
 * Se construye con el lenguaje visual del mockup —crema, filetes dorados,
 * versalitas con tracking y los mismos filetes de flecha de la sección de
 * entradas— y se coloca detrás de la tríada, justo antes del cierre.
 *
 * TODO sale de `event.program`: los cuatro bloques, sus títulos y sus horas.
 * Aquí no se teclea ni una hora ni un título (ADR-003 §2).
 */
export function ProgramaMexico({ event }: ProgramaMexicoProps) {
  const { programa } = MEXICO_COPY;

  return (
    <Section background="alt" spacing="tight" labelledBy={TITLE_ID} className={styles.section}>
      <Reveal className={styles.head}>
        <h2 id={TITLE_ID} className={styles.title}>
          <ArrowRule className={styles.arrow} />
          <span>{programa.eyebrow}</span>
          <ArrowRule direction="left" className={styles.arrow} />
        </h2>
        <p className={styles.lead}>{programa.lead}</p>
      </Reveal>

      <div className={styles.days}>
        {event.program.map((day, dayIndex) => (
          <section key={day.day} className={styles.day} aria-labelledby={`mexico-programa-dia-${day.day}`}>
            <h3 id={`mexico-programa-dia-${day.day}`} className={styles.dayHead}>
              <span className={styles.dayLabel}>{formatDayLabel(day)}</span>
              <span className={styles.dayDate}>{formatLongDate(day.date)}</span>
              <span className={styles.dayTitle}>{day.title}</span>
            </h3>

            <ol className={styles.blocks} role="list">
              {day.blocks.map((block, blockIndex) => (
                <ProgramItem
                  key={block.id}
                  block={block}
                  icon={BLOCK_ICONS[(dayIndex * 3 + blockIndex) % BLOCK_ICONS.length] ?? 'star'}
                  className={styles.block}
                />
              ))}
            </ol>
          </section>
        ))}
      </div>
    </Section>
  );
}
